import { fetchWrapper } from '~/utils/fetchWrapper'
import { extractApiError, extractApiErrorMessage } from '~/utils/shared'

interface SpeechToTextResponse {
  transcript?: string
  confidence?: number
  language_code?: string
}

export interface SpeechToTextResult {
  transcript: string
  confidence?: number
  languageCode?: string
}

interface TextToSpeechOptions {
  audioEncoding?: string
  languageCode?: string
  voiceName?: string
  signal?: AbortSignal
}

export interface TextToSpeechAudioSource {
  audio: HTMLAudioElement
  cleanup: () => void
  done: Promise<void>
}

function recordingExtension(mimeType: string): string {
  if (mimeType.includes('ogg')) return 'ogg'
  if (mimeType.includes('mp4')) return 'm4a'
  if (mimeType.includes('mpeg')) return 'mp3'
  return 'webm'
}

async function parseErrorResponse(response: Response, fallback: string): Promise<string> {
  try {
    const errorJson = JSON.parse(await response.text())
    return extractApiErrorMessage(errorJson, fallback)
  } catch {
    return fallback
  }
}

export async function speechToTextResult(
  workspaceUuid: string,
  audioBlob: Blob,
  languageCodes = ['auto']
): Promise<SpeechToTextResult> {
  const formData = new FormData()
  formData.append('audio', audioBlob, `recording.${recordingExtension(audioBlob.type)}`)

  const response = await fetchWrapper(
    `/api/workspace/${workspaceUuid}/speech-to-text?language_codes=${encodeURIComponent(languageCodes.join(','))}`,
    {
      method: 'POST',
      body: formData,
      timeoutMs: 90000,
      suppressErrorToast: true
    }
  )

  if (!response.ok) {
    let parsed = { message: `Speech transcription failed: ${response.statusText}` }
    try {
      parsed = extractApiError(await response.json(), parsed.message)
    } catch {
      // Fall through to the status text message.
    }
    throw new Error(parsed.message)
  }

  const data = (await response.json()) as SpeechToTextResponse
  return {
    transcript: (data.transcript || '').trim(),
    confidence: data.confidence,
    languageCode: data.language_code
  }
}

export async function speechToText(
  workspaceUuid: string,
  audioBlob: Blob,
  languageCodes = ['auto']
): Promise<string> {
  return (await speechToTextResult(workspaceUuid, audioBlob, languageCodes)).transcript
}

function abortError() {
  return new DOMException('The operation was aborted.', 'AbortError')
}

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw abortError()
  }
}

async function requestTextToSpeech(
  workspaceUuid: string,
  text: string,
  options: TextToSpeechOptions = {}
): Promise<Response> {
  const response = await fetchWrapper(`/api/workspace/${workspaceUuid}/text-to-speech`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      audio_encoding: options.audioEncoding || 'MP3',
      language_code: options.languageCode,
      voice_name: options.voiceName
    }),
    isStreaming: true,
    signal: options.signal,
    timeoutMs: 120000,
    suppressErrorToast: true
  })

  if (!response.ok) {
    const message = await parseErrorResponse(
      response,
      `Speech synthesis failed: ${response.statusText}`
    )
    throw new Error(message)
  }

  return response
}

async function readTextToSpeechBlob(response: Response, signal?: AbortSignal): Promise<Blob> {
  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('Speech synthesis returned no audio stream')
  }

  const chunks: Uint8Array[] = []
  while (true) {
    throwIfAborted(signal)
    const { done, value } = await reader.read()
    if (done) break
    if (value) chunks.push(value)
  }

  const contentType = response.headers.get('content-type') || 'audio/mpeg'
  // Same TS 5.7 BufferSource narrowing as in `appendSourceBuffer` below —
  // `Blob` accepts SharedArrayBuffer-backed views at runtime but the
  // updated lib types disallow them. Cast through `BlobPart[]`.
  return new Blob(chunks as unknown as BlobPart[], { type: contentType })
}

function getSupportedMediaSourceMimeType(contentType: string): string | null {
  const mediaSource = globalThis.MediaSource
  if (!mediaSource || typeof mediaSource.isTypeSupported !== 'function') {
    return null
  }

  const baseType = contentType.split(';')[0]?.trim().toLowerCase()
  const candidates =
    baseType === 'audio/mpeg'
      ? ['audio/mpeg', 'audio/mpeg; codecs="mp3"']
      : [contentType, baseType].filter(Boolean)

  return candidates.find(candidate => mediaSource.isTypeSupported(candidate)) ?? null
}

function appendSourceBuffer(
  sourceBuffer: SourceBuffer,
  value: Uint8Array,
  signal?: AbortSignal
): Promise<void> {
  throwIfAborted(signal)

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      sourceBuffer.removeEventListener('updateend', handleUpdateEnd)
      sourceBuffer.removeEventListener('error', handleError)
      signal?.removeEventListener('abort', handleAbort)
    }
    const handleUpdateEnd = () => {
      cleanup()
      resolve()
    }
    const handleError = () => {
      cleanup()
      reject(new Error('Failed to append speech audio chunk'))
    }
    const handleAbort = () => {
      cleanup()
      reject(abortError())
    }

    sourceBuffer.addEventListener('updateend', handleUpdateEnd, { once: true })
    sourceBuffer.addEventListener('error', handleError, { once: true })
    signal?.addEventListener('abort', handleAbort, { once: true })

    try {
      // `Uint8Array<ArrayBufferLike>` (TS 5.7+ default for stream readers)
      // isn't assignable to `BufferSource` because `BufferSource` excludes
      // SharedArrayBuffer-backed views. `appendBuffer` accepts both at
      // runtime, so cast through `BufferSource` rather than copying bytes.
      sourceBuffer.appendBuffer(value as unknown as BufferSource)
    } catch (error) {
      cleanup()
      reject(error)
    }
  })
}

function createMediaSourceAudioSource(
  response: Response,
  mimeType: string,
  signal?: AbortSignal
): TextToSpeechAudioSource {
  const mediaSource = new MediaSource()
  const audioUrl = URL.createObjectURL(mediaSource)
  const audio = new Audio(audioUrl)
  let reader: ReadableStreamDefaultReader<Uint8Array> | null = null

  const cleanup = () => {
    reader?.cancel().catch(() => undefined)
    reader = null
    URL.revokeObjectURL(audioUrl)
  }

  const done = new Promise<void>((resolve, reject) => {
    const handleAbort = () => {
      cleanup()
      reject(abortError())
    }

    signal?.addEventListener('abort', handleAbort, { once: true })

    mediaSource.addEventListener(
      'sourceopen',
      async () => {
        try {
          throwIfAborted(signal)
          const sourceBuffer = mediaSource.addSourceBuffer(mimeType)
          reader = response.body?.getReader() ?? null
          if (!reader) {
            throw new Error('Speech synthesis returned no audio stream')
          }

          while (true) {
            throwIfAborted(signal)
            const { done: isDone, value } = await reader.read()
            if (isDone) break
            if (value?.byteLength) {
              await appendSourceBuffer(sourceBuffer, value, signal)
            }
          }

          if (mediaSource.readyState === 'open') {
            mediaSource.endOfStream()
          }
          resolve()
        } catch (error) {
          if (mediaSource.readyState === 'open') {
            try {
              mediaSource.endOfStream('decode')
            } catch {
              // Ignore secondary cleanup errors.
            }
          }
          reject(error)
        } finally {
          signal?.removeEventListener('abort', handleAbort)
        }
      },
      { once: true }
    )
  })

  return { audio, cleanup, done }
}

async function createBufferedAudioSource(
  response: Response,
  signal?: AbortSignal
): Promise<TextToSpeechAudioSource> {
  const audioBlob = await readTextToSpeechBlob(response, signal)
  if (!audioBlob.size) {
    throw new Error('Speech synthesis returned no audio')
  }

  const audioUrl = URL.createObjectURL(audioBlob)
  return {
    audio: new Audio(audioUrl),
    cleanup: () => URL.revokeObjectURL(audioUrl),
    done: Promise.resolve()
  }
}

export async function createTextToSpeechAudio(
  workspaceUuid: string,
  text: string,
  options: TextToSpeechOptions = {}
): Promise<TextToSpeechAudioSource> {
  const response = await requestTextToSpeech(workspaceUuid, text, options)
  const contentType = response.headers.get('content-type') || 'audio/mpeg'
  const mediaSourceMimeType = getSupportedMediaSourceMimeType(contentType)

  if (mediaSourceMimeType && response.body) {
    return createMediaSourceAudioSource(response, mediaSourceMimeType, options.signal)
  }

  return createBufferedAudioSource(response, options.signal)
}

export async function textToSpeechStream(
  workspaceUuid: string,
  text: string,
  options: TextToSpeechOptions = {}
): Promise<Blob> {
  const response = await requestTextToSpeech(workspaceUuid, text, options)
  return readTextToSpeechBlob(response, options.signal)
}
